import React from 'react';

import {
  Box,
  Card,
  CardHeader,
  Divider,
  Typography,
} from '@mui/material';

import {
  ArrayField,
  Datagrid,
  DateField,
  FunctionField,
  ReferenceField,
  usePermissions,
  useRecordContext,
  RichTextField,
} from 'react-admin';

import hasAccess from '../../utilities/hasAccess';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import actions, { Actions } from '../../constants/actions';

import EditComment from './EditComment';
import CommentHistory from './CommentHistory';
import { IComment } from './IComments';
import CreateComment from './CreateComment';
import PermissionMissing from '../utils/PermissionMissing';
import DeleteComment from './DeleteComment';
import resourceNames from '../../constants/resourceNames';

export const CommentMessageField = ({
  emptyText,
  action,
}: {
  emptyText?: string;
  label: string;
  action?: Actions;
}) => {
  const comment = useRecordContext<IComment>();
  const { permissions } = usePermissions<Actions[]>();
  if (!comment) return null;

  const message = comment.editions?.length
    ? comment.editions[comment.editions.length - 1].message
    : comment.message;

  const isEdited = !!comment.editions?.length;

  return (
    <>
      <RichTextField
        source="message"
        record={{ message }}
        component="p"
        variant="body2"
        emptyText={emptyText}
      />
      <Typography component="p" variant="caption" color="grey" marginTop={2}>
        {isEdited ? (
          <>
            <Box component="span" sx={{ marginRight: '2px' }}>Edited</Box>
            {' - '}
            <CommentHistory />
            {action && hasAccess(permissions, action) && (
              <>
                {' - '}
                <EditComment />
              </>
            )}
          </>
        ) : action && hasAccess(permissions, action) && <EditComment />}
      </Typography>
    </>
  );
};

const CommentList = ({
  source,
  action,
  deleteAction,
}: {
  source: string;
  action: Actions;
  deleteAction: Actions;
}) => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Comments"
        action={hasAccess(permissions, action) && <CreateComment />}
      />
      <Divider />
      <ArrayField source={source}>
        <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
          <CommentMessageField emptyText="-" label="Comment" action={action} />
          <DateField showTime source="date" />
          {hasAccess(permissions, actions.MEMBER_GET) ? (
            <ReferenceField label="Author" source="member" reference={resourceNames.MEMBERS} link="edit">
              <FunctionField render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`} />
            </ReferenceField>
          ) : (<PermissionMissing />)}
          {hasAccess(permissions, deleteAction) && <DeleteComment parentId={record.id} />}
        </Datagrid>
      </ArrayField>
    </Card>
  );
};

export default CommentList;
